/**
 * SupplierExternalBookingModal ✦
 *
 * Supplier-authored booking. Mirrors the public BookingTokenPage
 * form but lives inside the supplier dashboard. Used when a booking
 * arrives off-platform — wedding fayre, phone, Instagram DM, the
 * "I met a couple at a wedding" pathway — and the supplier wants
 * the night inside EventDrop immediately without sending a
 * /book/:token invite for the client to fill in.
 *
 * Stages:
 *   1. compose — all booking fields + T&Cs + optional template pick
 *   2. ready   — show the generated /book/confirmation/:token URL
 *                with copy + resend-email actions
 *
 * Editorial restraint: calm walnut/charcoal surface, single primary
 * action per stage, no SaaS chrome. Out of scope for this phase:
 * SMS / WhatsApp dispatch, payment capture, e-signatures.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Copy, CheckCircle, X, EnvelopeSimple,
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CONFIRMATION_URL = (token) => {
  if (typeof window === 'undefined') return `/book/confirmation/${token}`;
  return `${window.location.origin}/book/confirmation/${token}`;
};

const SHELL_BG = '#15161B';
const RULE = 'rgba(194,157,82,0.22)';
const CREAM = '#F4E9D2';
const CREAM_MUTED = 'rgba(244,233,210,0.62)';
const GOLD = '#C29D52';

const labelStyle = {
  fontFamily: 'Arial, sans-serif',
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  color: GOLD,
  marginBottom: 6,
  display: 'block',
};

const inputStyle = {
  width: '100%',
  padding: '12px 14px',
  background: '#1B1C22',
  border: `1px solid ${RULE}`,
  borderRadius: 4,
  color: CREAM,
  fontFamily: 'Georgia, serif',
  fontSize: 15,
  outline: 'none',
};

const SupplierExternalBookingModal = ({ open, onClose, onCreated, prefillDate }) => {
  const [stage, setStage] = useState('compose'); // compose | ready
  const [draft, setDraft] = useState({
    client_name: '',
    client_email: '',
    client_mobile: '',
    event_date: '',
    event_time: '',
    event_type: '',
    venue: '',
    venue_address: '',
    access_issues: '',
    package_name: '',
    terms_text: '',
    supplier_note: '',
    terms_accepted: false,
    send_confirmation_now: true,
    template_id: null,
  });
  const [templates, setTemplates] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);
  const [resending, setResending] = useState(false);
  const [resentAt, setResentAt] = useState(null);

  // Prefill on open — pull the supplier's saved templates + default
  // terms so they don't retype the same boilerplate every time.
  useEffect(() => {
    if (!open) return;
    setStage('compose');
    setError('');
    setCreated(null);
    setCopied(false);
    setResentAt(null);
    setDraft((d) => ({
      ...d,
      event_date: prefillDate || d.event_date || '',
    }));
    (async () => {
      try {
        const [tplRes, defaultsRes] = await Promise.all([
          axios.get(`${API}/studio/booking-templates`).catch(() => ({ data: { templates: [] } })),
          axios.get(`${API}/studio/booking-defaults`).catch(() => ({ data: {} })),
        ]);
        setTemplates(tplRes.data?.templates || []);
        const def = defaultsRes.data || {};
        setDraft((d) => ({
          ...d,
          terms_text: d.terms_text || def.terms_text || '',
        }));
      } catch {
        /* quiet — templates are a nicety, not a requirement */
      }
    })();
  }, [open, prefillDate]);

  if (!open) return null;

  const applyTemplate = (tpl) => {
    if (!tpl) {
      setDraft((d) => ({ ...d, template_id: null }));
      return;
    }
    setDraft((d) => ({
      ...d,
      template_id: tpl.id,
      package_name: tpl.package_name || d.package_name,
      terms_text: tpl.terms_text || d.terms_text,
      supplier_note: tpl.supplier_note || d.supplier_note,
    }));
  };

  const submit = async () => {
    setError('');
    if (!draft.client_name.trim()) return setError('Client name is required');
    if (!draft.client_email.trim()) return setError('Client email is required');
    if (!draft.event_date.trim()) return setError('Event date is required');
    if (!draft.venue.trim()) return setError('Venue is required');
    if (!draft.package_name.trim()) return setError('Package / service is required');
    if (!draft.terms_text.trim()) return setError('Terms text is required');
    if (!draft.terms_accepted) return setError('Confirm the client has agreed to the terms');
    setBusy(true);
    try {
      const r = await axios.post(`${API}/studio/bookings/external`, {
        client_name: draft.client_name.trim(),
        client_email: draft.client_email.trim().toLowerCase(),
        client_mobile: draft.client_mobile.trim(),
        event_date: draft.event_date.trim(),
        event_time: draft.event_time.trim(),
        event_type: draft.event_type.trim(),
        venue: draft.venue.trim(),
        venue_address: draft.venue_address.trim(),
        access_issues: draft.access_issues.trim(),
        package_name: draft.package_name.trim(),
        terms_text: draft.terms_text.trim(),
        supplier_note: draft.supplier_note.trim(),
        terms_accepted: true,
        template_id: draft.template_id,
        send_confirmation_now: draft.send_confirmation_now,
      });
      setCreated(r.data);
      setStage('ready');
      if (onCreated) onCreated(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not create booking. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const copyConfirmation = async () => {
    if (!created?.booking_link?.token) return;
    const url = CONFIRMATION_URL(created.booking_link.token);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard blocked — leave the URL visible for manual copy */
    }
  };

  const resendEmail = async () => {
    if (!created?.booking_link?.id) return;
    setResending(true);
    try {
      await axios.post(`${API}/studio/booking-links/${created.booking_link.id}/resend-confirmation`);
      setResentAt(new Date().toLocaleTimeString());
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not resend confirmation');
    } finally {
      setResending(false);
    }
  };

  return (
    <div
      data-testid="supplier-external-booking-modal"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 50,
        background: 'rgba(12,8,4,0.78)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16, overflowY: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 640, background: SHELL_BG,
          border: `1px solid ${RULE}`, borderRadius: 8,
          color: CREAM, fontFamily: 'Georgia, serif',
          maxHeight: '94vh', overflowY: 'auto',
        }}
      >
        {/* Header */}
        <div style={{ padding: '28px 32px 8px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
          <div>
            <div style={{ width: 42, height: 1, background: GOLD, marginBottom: 14 }} />
            <p style={labelStyle}>✦ Create External Booking</p>
            <h2 style={{
              margin: '8px 0 0', fontSize: 24, fontWeight: 500,
              letterSpacing: '-0.012em', lineHeight: 1.22,
            }}>
              {stage === 'compose'
                ? 'A booking that arrived another way ✦'
                : 'The night is now in your diary ✦'}
            </h2>
          </div>
          <button
            onClick={onClose}
            data-testid="external-booking-close"
            style={{ background: 'transparent', border: 'none', color: CREAM_MUTED, cursor: 'pointer' }}
            aria-label="Close"
          >
            <X size={20} weight="bold" />
          </button>
        </div>

        {stage === 'compose' && (
          <div style={{ padding: '20px 32px 32px' }}>
            <p style={{ color: CREAM_MUTED, fontSize: 14, lineHeight: 1.55, marginTop: 0, marginBottom: 24 }}>
              For bookings that arrived by phone, at a fayre, or through a DM. Drop the details in,
              tick that the client agreed to the terms, and the night lands directly in your diary.
            </p>

            {templates.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <label style={labelStyle}>Reuse template (optional)</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {templates.map((tpl) => (
                    <button
                      key={tpl.id}
                      onClick={() => applyTemplate(draft.template_id === tpl.id ? null : tpl)}
                      data-testid={`ext-template-${tpl.id}`}
                      style={{
                        padding: '6px 12px', borderRadius: 999, fontSize: 12,
                        fontFamily: 'Arial, sans-serif', fontWeight: 700,
                        letterSpacing: '0.06em',
                        background: draft.template_id === tpl.id ? GOLD : 'transparent',
                        color: draft.template_id === tpl.id ? '#1A1208' : CREAM,
                        border: `1px solid ${draft.template_id === tpl.id ? GOLD : RULE}`,
                        cursor: 'pointer',
                      }}
                    >
                      {tpl.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
              <div>
                <label style={labelStyle} htmlFor="ext-client-name">Client name</label>
                <input
                  id="ext-client-name"
                  data-testid="ext-client-name"
                  style={inputStyle}
                  value={draft.client_name}
                  onChange={(e) => setDraft({ ...draft, client_name: e.target.value })}
                />
              </div>
              <div>
                <label style={labelStyle} htmlFor="ext-client-email">Client email</label>
                <input
                  id="ext-client-email"
                  data-testid="ext-client-email"
                  type="email"
                  style={inputStyle}
                  value={draft.client_email}
                  onChange={(e) => setDraft({ ...draft, client_email: e.target.value })}
                />
              </div>
              <div>
                <label style={labelStyle} htmlFor="ext-client-mobile">Client mobile (optional)</label>
                <input
                  id="ext-client-mobile"
                  data-testid="ext-client-mobile"
                  style={inputStyle}
                  value={draft.client_mobile}
                  onChange={(e) => setDraft({ ...draft, client_mobile: e.target.value })}
                  placeholder="+44 …"
                />
              </div>
              <div>
                <label style={labelStyle} htmlFor="ext-event-date">Event date</label>
                <input
                  id="ext-event-date"
                  data-testid="ext-event-date"
                  type="date"
                  style={inputStyle}
                  value={draft.event_date}
                  onChange={(e) => setDraft({ ...draft, event_date: e.target.value })}
                />
              </div>
              <div>
                <label style={labelStyle} htmlFor="ext-event-time">Time (optional)</label>
                <input
                  id="ext-event-time"
                  data-testid="ext-event-time"
                  style={inputStyle}
                  value={draft.event_time}
                  onChange={(e) => setDraft({ ...draft, event_time: e.target.value })}
                  placeholder="setup 5pm, doors 7pm"
                />
              </div>
              <div>
                <label style={labelStyle} htmlFor="ext-event-type">Event type (optional)</label>
                <input
                  id="ext-event-type"
                  data-testid="ext-event-type"
                  style={inputStyle}
                  value={draft.event_type}
                  onChange={(e) => setDraft({ ...draft, event_type: e.target.value })}
                  placeholder="Wedding, Birthday, Anniversary…"
                />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle} htmlFor="ext-venue">Venue</label>
                <input
                  id="ext-venue"
                  data-testid="ext-venue"
                  style={inputStyle}
                  value={draft.venue}
                  onChange={(e) => setDraft({ ...draft, venue: e.target.value })}
                />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle} htmlFor="ext-venue-address">Venue address (optional)</label>
                <input
                  id="ext-venue-address"
                  data-testid="ext-venue-address"
                  style={inputStyle}
                  value={draft.venue_address}
                  onChange={(e) => setDraft({ ...draft, venue_address: e.target.value })}
                />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle} htmlFor="ext-access">Access notes (optional)</label>
                <input
                  id="ext-access"
                  data-testid="ext-access-issues"
                  style={inputStyle}
                  value={draft.access_issues}
                  onChange={(e) => setDraft({ ...draft, access_issues: e.target.value })}
                  placeholder="Step-free? Loading bay? Parking?"
                />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle} htmlFor="ext-package">Package / service</label>
                <input
                  id="ext-package"
                  data-testid="ext-package"
                  style={inputStyle}
                  value={draft.package_name}
                  onChange={(e) => setDraft({ ...draft, package_name: e.target.value })}
                  placeholder="e.g. Full Day Photography"
                />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle} htmlFor="ext-note">Supplier note (optional)</label>
                <textarea
                  id="ext-note"
                  data-testid="ext-supplier-note"
                  rows={3}
                  style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.5 }}
                  value={draft.supplier_note}
                  onChange={(e) => setDraft({ ...draft, supplier_note: e.target.value })}
                  placeholder="A quiet line that will appear on the confirmation."
                />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle} htmlFor="ext-terms">Terms & conditions</label>
                <textarea
                  id="ext-terms"
                  data-testid="ext-terms-text"
                  rows={5}
                  style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.55 }}
                  value={draft.terms_text}
                  onChange={(e) => setDraft({ ...draft, terms_text: e.target.value })}
                  placeholder="What the client is agreeing to."
                />
              </div>
            </div>

            {/* Terms ack + send-now toggles */}
            <div style={{ marginTop: 22, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  data-testid="ext-terms-accepted"
                  checked={draft.terms_accepted}
                  onChange={(e) => setDraft({ ...draft, terms_accepted: e.target.checked })}
                  style={{ marginTop: 4, accentColor: GOLD }}
                />
                <span style={{ fontSize: 13.5, lineHeight: 1.5, color: CREAM }}>
                  The client has agreed to these terms ✦
                </span>
              </label>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  data-testid="ext-send-now"
                  checked={draft.send_confirmation_now}
                  onChange={(e) => setDraft({ ...draft, send_confirmation_now: e.target.checked })}
                  style={{ marginTop: 4, accentColor: GOLD }}
                />
                <span style={{ fontSize: 13.5, lineHeight: 1.5, color: CREAM_MUTED }}>
                  Send confirmation email to client now
                </span>
              </label>
            </div>

            {error && (
              <p data-testid="ext-error" style={{ marginTop: 16, color: '#F87171', fontSize: 13 }}>
                {error}
              </p>
            )}

            <div style={{ marginTop: 26, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button
                onClick={submit}
                disabled={busy}
                data-testid="ext-submit"
                style={{
                  padding: '13px 26px', background: GOLD, color: '#1A1208',
                  border: 'none', borderRadius: 3,
                  fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 15,
                  cursor: busy ? 'wait' : 'pointer', opacity: busy ? 0.7 : 1,
                }}
              >
                {busy ? 'Settling…' : 'Create booking ✦'}
              </button>
              <button
                onClick={onClose}
                style={{
                  padding: '13px 22px', background: 'transparent', color: CREAM_MUTED,
                  border: `1px solid ${RULE}`, borderRadius: 3,
                  fontFamily: 'Georgia, serif', fontSize: 14, cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {stage === 'ready' && created && (
          <div style={{ padding: '20px 32px 32px' }}>
            <p style={{ color: CREAM_MUTED, fontSize: 14, lineHeight: 1.6, marginTop: 0 }}>
              <em>{draft.client_name}</em>'s booking is settled. The confirmation link is below — copy
              it to share quietly, or resend the email if needed.
            </p>

            <div style={{
              marginTop: 22, padding: '14px 16px',
              background: '#1B1C22', border: `1px solid ${RULE}`, borderRadius: 4,
              fontFamily: 'Arial, sans-serif', fontSize: 13,
              color: CREAM, wordBreak: 'break-all',
            }}>
              {CONFIRMATION_URL(created.booking_link.token)}
            </div>

            <div style={{ marginTop: 16, display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              <button
                onClick={copyConfirmation}
                data-testid="ext-copy-confirmation"
                style={{
                  padding: '11px 18px', background: GOLD, color: '#1A1208',
                  border: 'none', borderRadius: 3,
                  fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: 14,
                  cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8,
                }}
              >
                {copied ? <CheckCircle size={14} weight="fill" /> : <Copy size={14} weight="bold" />}
                {copied ? 'Copied ✦' : 'Copy confirmation link ✦'}
              </button>
              <button
                onClick={resendEmail}
                disabled={resending}
                data-testid="ext-resend-email"
                style={{
                  padding: '11px 18px', background: 'transparent', color: CREAM,
                  border: `1px solid ${RULE}`, borderRadius: 3,
                  fontFamily: 'Georgia, serif', fontSize: 14,
                  cursor: resending ? 'wait' : 'pointer', opacity: resending ? 0.7 : 1,
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                }}
              >
                <EnvelopeSimple size={14} weight="bold" />
                {resending ? 'Sending…' : (resentAt ? `Resent · ${resentAt}` : 'Resend by email ✦')}
              </button>
              <a
                href={`${API}/book/${created.booking_link.token}/confirmation.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="ext-download-pdf"
                style={{
                  padding: '11px 18px', background: 'transparent', color: CREAM,
                  border: `1px solid ${RULE}`, borderRadius: 3,
                  fontFamily: 'Georgia, serif', fontSize: 14,
                  textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 8,
                }}
              >
                Download PDF ✦
              </a>
            </div>

            <div style={{ marginTop: 28, display: 'flex', gap: 10 }}>
              <button
                onClick={onClose}
                data-testid="ext-done"
                style={{
                  padding: '11px 22px', background: 'transparent', color: CREAM_MUTED,
                  border: `1px solid ${RULE}`, borderRadius: 3,
                  fontFamily: 'Georgia, serif', fontSize: 14, cursor: 'pointer',
                }}
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupplierExternalBookingModal;
